// Tooltip body content for the /positions and /trades section help icons.
// Pure JSX — no logic. Kept separate from cardKit so the primitives stay
// content-agnostic and these blurbs can be edited without touching layout.

import type { ReactNode } from 'react';

function Term({ children }: { children: ReactNode }) {
  return <span className="text-foreground font-medium">{children}</span>;
}

export function PositionsHelp() {
  return (
    <>
      <p>Contracts you currently hold. Card and table show the same data — only the layout differs.</p>
      <div className="mt-2 space-y-1">
        <p>
          <Term>side</Term>: YES/NO + team chip.{' '}
          <span className="text-blue-300">K</span> = Kalshi,{' '}
          <span className="text-blue-300">P</span> = Polymarket.
        </p>
        <p>
          <Term>qty</Term>: contracts held. An amber{' '}
          <span className="text-amber-400">(n)</span> after the number = contracts already on open sell orders.
        </p>
        <p>
          <Term>avg</Term>: your average entry price per contract.
        </p>
        <p>
          <Term>now</Term>: current market mark, side-native cents.{' '}
          <span className="text-red-400">red</span> low →{' '}
          <span className="text-yellow-400">yellow</span> mid →{' '}
          <span className="text-green-400">green</span> high.
        </p>
        <p>
          <Term>cost</Term>: avg × qty + entry fees.
        </p>
        <p>
          <Term>P&L</Term>: live unrealized = cash out − cost.{' '}
          <span className="text-positive">green</span> positive,{' '}
          <span className="text-negative">red</span> negative.
        </p>
        <p>
          <Term>cash out</Term>: liquidation value at the current mark, minus the estimated close fee.
        </p>
        <p>
          <Term>if win</Term>: net profit if the contract settles YES.{' '}
          <em>pays $1.00</em> = gross settlement payout; the (±odds) = implied American.
        </p>
      </div>
      <p className="mt-2 text-[11px]">
        Polymarket positions render "—" for now / P&L / cash out / if win — Hydra doesn't price the per-token mark and Poly's close-fee schedule isn't trusted enough to estimate.
      </p>
    </>
  );
}

export function RecyclerHelp() {
  return (
    <>
      <p>
        Each bot stakes out a spot in a market's queue and <Term>recycles</Term> it — rotating its
        front-most chunk to the back <em>before</em> it fills, so it holds priority while costing
        nothing. Same data in every view; only the layout differs.
      </p>
      <div className="mt-2 space-y-1">
        <p>
          <Term>the fill bar</Term>: the colored background on each item shows how far an order has
          climbed toward the front — a visual anchor that never hides the numbers.
        </p>
        <p>
          <Term>front</Term>: contracts ahead of you at your price — how close you are to a fill.{' '}
          <span className="text-green-400">green</span> deep, safe →{' '}
          <span className="text-amber-400">amber</span> near →{' '}
          <span className="text-red-400">red</span> at the front.
        </p>
        <p>
          <Term>climb / fill ETA</Term>: how fast you're advancing and the estimated seconds to the
          front, from how quickly the line ahead is clearing. Marked <em>~est</em> — a live estimate,
          not a server number.
        </p>
        <p>
          <Term>auto-recycle vs let it ride</Term>: auto-recycle rotates the chunk back before it
          fills (holds the spot, costs nothing). Let it ride lets it reach the front and fill —
          turning staked priority into a real position.
        </p>
        <p>
          <Term>claim / release</Term>: claim a pooled bot order under your name (it stays on the org
          keys, just attributed to you); release it back to the pool any time before it fills.
        </p>
        <p>
          <Term>posture</Term>: the bot's own health, separate from queue color.{' '}
          <span className="text-green-400">green</span> healthy,{' '}
          <span className="text-amber-400">amber</span> working,{' '}
          <span className="text-red-400">red</span> trouble.
        </p>
        <p>
          <Term>markout</Term>: fill quality 2s/5s/10s after a fill — whether fills land on the right
          side.
        </p>
      </div>
      <p className="mt-2 text-[11px]">
        Claim/release is live (relay-enforced attribution + authz). Per-order let-it-ride is
        rendered but inert until the edge ships a per-order control endpoint — everything else
        runs on live g10edge data.
      </p>
    </>
  );
}

export function OrdersHelp() {
  return (
    <>
      <p>Resting and partially-filled limit orders. Same data in card and table.</p>
      <div className="mt-2 space-y-1">
        <p>
          <span className="text-blue-300">RESTING</span> sits in the book.{' '}
          <span className="text-amber-300">IN SALE</span> = actual partial fill with no better price level ahead (amber card border / amber table rail).
        </p>
        <p>
          <Term>side</Term>: YES/NO + team. Polymarket side labels appear when the CLOB token id resolves to a known market.
        </p>
        <p>
          <Term>venue</Term>:{' '}
          <span className="text-blue-300">K</span> = Kalshi,{' '}
          <span className="text-blue-300">P</span> = Polymarket.
        </p>
        <p>
          <Term>book</Term>:{' '}
          <span className="text-green-400">At front</span> means at the best bid/ask price level; <Term>queue</Term> still shows FIFO depth at that price.{' '}
          <span className="text-yellow-300">Behind 1¢</span>,{' '}
          <span className="text-red-400">Behind ≥2¢</span>.
        </p>
        <p>
          <Term>queue</Term>: your absolute spot in the FIFO — your slot at your price level plus every contract resting at strictly better prices. 0 = literal front of book.
        </p>
        <p>
          <Term>level above</Term>: contracts at better price level(s) than your order — those clear before your level is touched. 0 = top price.
        </p>
        <p>
          <Term>in line behind</Term>: contracts at your exact price level resting behind you in the FIFO, excluding your own remaining order size.
        </p>
        <p>
          <Term>queue</Term> and <Term>level above</Term> color-code:{' '}
          <span className="text-green-400">green</span> &lt;50k →{' '}
          <span className="text-yellow-400">yellow</span> 50–300k →{' '}
          <span className="text-red-400">red</span> ≥300k. The row tint and value pulse track <Term>queue</Term> — that's the metric a trader's eyes should snap to.
        </p>
        <p>
          <Term>in line behind</Term> uses the inverse color scale: larger depth behind your order is better.
        </p>
        <p>
          <Term>bid / ask</Term>: your limit price + implied American odds.
        </p>
        <p>
          <Term>last</Term>: most recent trade price on the same side.
        </p>
        <p>
          <Term>cost / to win</Term>: current order economics for the working or actually-filled quantity, including known Kalshi maker fees when available.
        </p>
        <p>
          <Term>5m / 30m / 1h</Term>: queue movement when resting; contracts filled when in-sale. Accumulates while the page is open.
        </p>
        <p>
          <Term>resting Xh</Term>: time since placement.{' '}
          <Term>cancel</Term> pulls the order.
        </p>
      </div>
      <p className="mt-2 text-[11px]">
        Polymarket orders show token-resolved side labels when available; last / book / queue / level above / in line behind remain "—" until Poly CLOB queue plumbing exists.
      </p>
    </>
  );
}

export function TradesHelp() {
  return (
    <>
      <p>
        Closed contracts — settled or sold. Same data in card and table; the active{' '}
        <Term>Audit</Term> / <Term>Trader</Term> tab picks the data shape.
      </p>
      <div className="mt-2 space-y-1">
        <p>
          <Term>action</Term>:{' '}
          <span className="text-blue-300">SETTLED</span> (game finished),{' '}
          <span className="text-violet-300">SELL</span> (you closed it),{' '}
          <span className="text-amber-300">OPEN</span> (still held — only visible in the <em>All</em> filter),{' '}
          <span className="text-red-300">REJECTED</span> (order never filled).
        </p>
        <p>
          <Term>side</Term>: YES/NO + team.{' '}
          <span className="text-blue-300">K</span> = Kalshi.
        </p>
        <p>
          <Term>qty / entry / exit</Term>: contracts and average prices in/out.
        </p>
        <p>
          <Term>fees</Term>: total fees paid (entry + exit).
        </p>
        <p>
          <Term>closed X ago</Term>: time since the contract closed.
        </p>
        <p>
          <Term>net P&L</Term>: realized profit/loss after fees.{' '}
          <span className="text-positive">green</span> positive,{' '}
          <span className="text-negative">red</span> negative.
        </p>
      </div>
    </>
  );
}

export function G9Help() {
  return (
    <>
      <p>
        Josh's Kalshi auto-assist for testing underdog scalp setups. You still pick the trade;
        G9 only arms the entry and exit rules you enter.
      </p>
      <div className="mt-2 space-y-1">
        <p>
          <Term>rows</Term>: each matchup is a paired row. The two team rows belong together,
          and each team has its own Kalshi <Term>Y</Term> and <Term>N</Term> chits.
        </p>
        <p>
          <Term>Y / N</Term>: buy YES or NO on that team's ticker. A NO on one player is
          effectively a bet that the other player wins.
        </p>
        <p>
          <Term>bid / ask</Term>: current cents on that chit. Clicking one selects the exact
          team + side in the sidebar and seeds entry / target from the live ask.
        </p>
        <p>
          <Term>start</Term>: start-time labels help avoid stale markets.{' '}
          <span className="text-amber-300">JUST STARTED</span> is usually more useful for this
          strategy than <span className="text-red-300">OLD START</span>.
        </p>
        <p>
          <Term>setup</Term>: quick read on price shape and match age. It is decision support,
          not a guarantee that the scalp is good.
        </p>
        <p>
          <Term>entry / target / qty</Term>: cents and contracts. Trigger waits for ask &lt;=
          entry and can fire immediately if entry is already at the ask; Resting places the
          entry limit immediately.
        </p>
        <p>
          <Term>auto-cancel</Term>: unchecked means no timeout. Checked cancels an armed or
          working entry after the minute count.
        </p>
        <p>
          <Term>limits</Term>: header position / contract caps still apply. G9 trades Kalshi
          only for now; Polymarket is not wired here yet.
        </p>
        <p>
          <Term>cancel</Term>: pulls the live G9 order. It does not guarantee you are flat if
          an entry already filled.
        </p>
        <p>
          <Term>server runner</Term>: once armed, the relay owns the rule and keeps checking
          for entry fills / target sells even if this page is closed.
        </p>
      </div>
    </>
  );
}
